import { FC, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import { AxiosError } from "axios";
import { Model, Question, SurveyModel } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/survey-core.min.css";
import {
  selectFormTemplateById,
  setFormSubmissions
} from "store/reducers/formTemplates/FormTemplatesSlice";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import {
  postFormSubmission,
  getFormSubmissions,
  FormAnswer
} from "apis/v3/formTemplatesApi";
import { trackFormStarted, trackFormSubmissionCompleted } from "analytics";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import GLButton from "components/v3/sharedComponents/GLButton";
import { COLORS } from "components/v3/Theme/colors";
import SurveyWrapperStyles from "components/v3/sharedComponents/surveys/SurveyWrapper/SurveyWrapper.styles";
import "components/v3/sharedComponents/forms/CustomPanelRenderer";
import "components/v3/sharedComponents/forms/CustomCheckboxWithButton";
import "components/v3/sharedComponents/surveys/CustomQuestions/registerChoicePillQuestions";
import "components/v3/sharedComponents/surveys/CustomQuestions/registerGlBoolean";
import { enableSurveyTitleMarkdown } from "components/v3/sharedComponents/surveys/enableSurveyTitleMarkdown";

export interface FormData {
  formId: string;
  formVersion: string;
  formAnswers: FormAnswer[];
}

interface FormTemplateWrapperProps {
  formId: string;
  onComplete?: (survey: SurveyModel) => void | Promise<void>;
  onSubmitting?: () => void;
  deferSubmission?: boolean;
  onCompleteWithData?: (data: FormData) => void;
  templateRootClassName?: string;
  preCheckConfirmationQuestion?: boolean;
  formJsonOverride?: Record<string, unknown>;
  formVersionOverride?: string;
  onSurveyInstantiated?: (survey: SurveyModel) => void;
  /** When true, renders SurveyJS bottom navigation (Continue / Complete). Default false. */
  showNavigationButtons?: boolean;
  /** When true with navigation, enables/disables Continue based on page validation. */
  overrideNextButtonEnabled?: boolean;
  /** Optional cancel handler; renders a cancel button beside Complete when navigation is shown. */
  onCancel?: () => void;
  cancelButtonText?: string;
}

const FormTemplateWrapper: FC<FormTemplateWrapperProps> = ({
  formId,
  onComplete,
  onSubmitting,
  deferSubmission = false,
  onCompleteWithData,
  templateRootClassName,
  preCheckConfirmationQuestion = false,
  formJsonOverride,
  formVersionOverride,
  onSurveyInstantiated,
  showNavigationButtons = false,
  overrideNextButtonEnabled = false,
  onCancel,
  cancelButtonText = "Cancel"
}) => {
  const classes = SurveyWrapperStyles();
  const [surveyModel, setSurveyModel] = useState<SurveyModel | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const [canComplete, setCanComplete] = useState(false);
  const dispatch = useDispatch();
  const onSurveyInstantiatedRef = useRef(onSurveyInstantiated);
  onSurveyInstantiatedRef.current = onSurveyInstantiated;
  const trackedFormStartKey = useRef<string | null>(null);
  const useCustomFormActions = Boolean(onCancel && showNavigationButtons);

  const formTemplate = useSelector(selectFormTemplateById(formId));

  const updateCanComplete = useCallback(
    (survey: SurveyModel): void => {
      if (!overrideNextButtonEnabled && !useCustomFormActions) {
        return;
      }

      setCanComplete(!survey.hasErrors(false, false));
    },
    [overrideNextButtonEnabled, useCustomFormActions]
  );

  useEffect(() => {
    const content = formJsonOverride
      ? formJsonOverride
      : formTemplate?.content
        ? JSON.parse(formTemplate.content)
        : null;

    if (!content) {
      return;
    }

    const formVersion = formTemplate?.version ?? formVersionOverride ?? "1.0.0";
    const formStartKey = `${formId}@${formVersion}`;
    if (trackedFormStartKey.current !== formStartKey) {
      trackedFormStartKey.current = formStartKey;
      trackFormStarted({ formId, formVersion });
    }

    const model = new Model(content);
    model.showCompletedPage = false;
    model.showNavigationButtons =
      showNavigationButtons && !useCustomFormActions ? "bottom" : "none";
    if (overrideNextButtonEnabled || useCustomFormActions) {
      // Commit text values per keystroke so the complete button enables as the
      // member finishes the last field instead of after they blur it.
      model.textUpdateMode = "onTyping";
    }
    enableSurveyTitleMarkdown(model);

    if (preCheckConfirmationQuestion) {
      const qName = `${formId}-confirmation`;
      const q = model.getQuestionByName(qName);
      if (q?.getType() === "checkbox-with-button") {
        const choice = (q as { choices?: { value: string }[] }).choices?.[0];
        if (choice?.value !== undefined) {
          model.setValue(qName, [choice.value]);
        }
      }
    }

    onSurveyInstantiatedRef.current?.(model);
    updateCanComplete(model);
    setSurveyModel(model);
  }, [
    formTemplate,
    formId,
    preCheckConfirmationQuestion,
    formJsonOverride,
    formVersionOverride,
    showNavigationButtons,
    overrideNextButtonEnabled,
    useCustomFormActions,
    updateCanComplete
  ]);

  const setNextButtonAvailability = (survey: SurveyModel): void => {
    if (useCustomFormActions) {
      updateCanComplete(survey);
      return;
    }

    if (!overrideNextButtonEnabled) {
      return;
    }

    setTimeout(() => {
      const nextButton = document.querySelector<HTMLButtonElement>(
        ".sd-navigation__next-btn"
      );
      const completeButton = document.querySelector<HTMLButtonElement>(
        ".sd-navigation__complete-btn"
      );
      const hasErrors = survey.currentPage.hasErrors(false, false);

      if (nextButton) {
        nextButton.disabled = hasErrors;
      }
      if (completeButton) {
        completeButton.disabled = hasErrors;
      }
    }, 100);
  };

  const handleCustomComplete = (): void => {
    if (surveyModel?.validate()) {
      // Submit without completing the SurveyJS model so a later API
      // failure can leave the questions on screen for retry.
      handleComplete(surveyModel);
    }
  };

  const handleValueChanged = (survey: SurveyModel): void => {
    setNextButtonAvailability(survey);
  };

  const handleCurrentPageChanged = (survey: SurveyModel): void => {
    setNextButtonAvailability(survey);
  };

  const handleAfterRenderPage = (survey: SurveyModel): void => {
    setNextButtonAvailability(survey);
  };

  const handleAfterRenderQuestion = (
    _survey: SurveyModel,
    options: { question: Question; htmlElement: HTMLElement }
  ): void => {
    if (!overrideNextButtonEnabled && !useCustomFormActions) {
      return;
    }
    const { question } = options;
    // SurveyJS ignores textUpdateMode="onTyping" for tel/email/url inputs and
    // commits them on blur, so the complete button would stay disabled until
    // the member left the last field. Mirror those values as they type; the
    // button check uses hasErrors(false) so no validation message is shown.
    if (question.getType() !== "text" || question.isInputTextUpdate) {
      return;
    }
    const input = options.htmlElement.querySelector("input");
    input?.addEventListener("input", (event) => {
      question.value = (event.target as HTMLInputElement).value;
    });
  };

  const handleComplete = (survey: SurveyModel) => {
    const formAnswers: FormAnswer[] = Object.entries(survey.data).flatMap(
      (answer) => {
        const questionId = answer[0];
        const value = answer[1] as string | boolean | string[];
        const question = survey.getQuestionByName(questionId);
        if (!question) {
          return [];
        }
        const title = question.title || "";
        const displayValue =
          question.getDisplayValue(false) === "Other (describe)"
            ? "Other"
            : question.getDisplayValue(false);

        const textArray = Array.isArray(value)
          ? displayValue.split(",").map((t: string) => t.trim())
          : displayValue;

        const flattenSingleItem = (val: any) =>
          Array.isArray(val) && val.length === 1 ? val[0] : val;

        const finalValue = flattenSingleItem(value);
        const text = flattenSingleItem(textArray);

        const questionJson = question.toJSON();
        const formattedQuestion = {
          ...questionJson,
          type: question.getType(),
          isRequired: question.isRequired,
          isVisible: question.isVisible
        };

        return [
          {
            questionId,
            title,
            value: JSON.stringify(finalValue),
            text: JSON.stringify(text),
            questionAsAsked: JSON.stringify(formattedQuestion)
          }
        ];
      }
    );

    const formVersion = formTemplate?.version ?? formVersionOverride ?? "1.0.0";

    if (deferSubmission && onCompleteWithData) {
      onCompleteWithData({ formId, formVersion, formAnswers });
      return;
    }

    if (onSubmitting) {
      onSubmitting();
    }

    setSubmitting(true);

    let formSaved = false;

    postFormSubmission({
      formId,
      formAnswers
    })
      .then((response) => {
        if (response instanceof AxiosError) {
          throw response;
        }
        return getFormSubmissions();
      })
      .then((response) => {
        if (response instanceof AxiosError) {
          throw response;
        }
        dispatch(setFormSubmissions(response.data));
        formSaved = true;
        trackFormSubmissionCompleted({
          formId,
          formVersion,
          outcome: "success"
        });
        return onComplete?.(survey);
      })
      .then(() => {
        setSubmitting(false);
      })
      .catch((error) => {
        console.error("Error submitting form:", error);
        setSubmitting(false);

        if (!formSaved) {
          trackFormSubmissionCompleted({
            formId,
            formVersion,
            outcome: "failure",
            errorType: error instanceof Error ? error.message : "unknown_error"
          });
        }

        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "Something went wrong",
            subText: "Please try again."
          })
        );
      });
  };

  if (!formJsonOverride && !formTemplate) {
    return (
      <Grid
        container
        className={classes.loadingContainer}
        sx={{ position: "relative" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <LoadingAnimation />
        </Box>
      </Grid>
    );
  }

  if (!surveyModel || submitting) {
    return (
      <Grid
        container
        className={classes.loadingContainer}
        sx={{ position: "relative" }}
      >
        <Box
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)"
          }}
        >
          <LoadingAnimation />
        </Box>
      </Grid>
    );
  }

  const compactSurveyLayout = Boolean(templateRootClassName);

  return (
    <Grid
      container
      className={`${classes.surveyContainer} ${classes.formTemplateContainer}${
        templateRootClassName ? ` ${templateRootClassName}` : ""
      }`}
      sx={
        compactSurveyLayout
          ? {
              display: "flex",
              flex: "1 1 0%",
              flexDirection: "column",
              height: 0,
              minHeight: 0,
              overflow: "hidden",
              width: "100%"
            }
          : undefined
      }
    >
      <Box
        sx={{
          width: "100%",
          ...(compactSurveyLayout
            ? {
                display: "flex",
                flex: "1 1 0%",
                flexDirection: "column",
                minHeight: 0,
                overflow: "hidden"
              }
            : {})
        }}
      >
        <Box
          sx={{
            position: "relative",
            ...(compactSurveyLayout
              ? {
                  display: "flex",
                  flex: "1 1 0%",
                  flexDirection: "column",
                  minHeight: 0,
                  overflow: "hidden"
                }
              : {})
          }}
        >
          <Survey
            model={surveyModel}
            onComplete={handleComplete}
            onValueChanged={handleValueChanged}
            onCurrentPageChanged={handleCurrentPageChanged}
            onAfterRenderPage={handleAfterRenderPage}
            onAfterRenderQuestion={handleAfterRenderQuestion}
          />
          {useCustomFormActions ? (
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: { sm: "row", xs: "column-reverse" },
                gap: 2,
                justifyContent: "flex-end",
                pt: 2,
                width: "100%"
              }}
            >
              <GLButton
                buttonType="secondary-no-border"
                onClick={onCancel}
                sx={{
                  typography: "body_strong_dark_gray",
                  "&:hover:not(.Mui-disabled)": {
                    backgroundColor: "transparent"
                  }
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: `${COLORS.DARK_GRAY} !important`,
                    fontSize: "16px !important",
                    typography: "body_strong_dark_gray"
                  }}
                >
                  {cancelButtonText}
                </Typography>
              </GLButton>
              <GLButton
                disabled={!canComplete}
                onClick={handleCustomComplete}
                rightIcon={<ArrowForward />}
                text={surveyModel.completeText}
              />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Grid>
  );
};

export default FormTemplateWrapper;
